import type { SxProps, Theme } from '@mui/material';
import { Box } from '@mui/material';

/**
 * Wraps content in a scrollable area with a gradient fade at the bottom.
 * Use when you want to cap height and suggest more content without a harsh cut.
 * The scrollable region is (maxHeight - fadeHeight) so the last visible line is not covered by the gradient.
 */
export function FadedText({
  children,
  maxHeight = 100,
  fadeHeight = 16,
  sx,
}: {
  /** Content to show (e.g. Typography or plain text). */
  children: React.ReactNode;
  /** Max total height in px (scroll area + gradient strip). */
  maxHeight?: number;
  /** Height of the gradient strip in px. */
  fadeHeight?: number;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box
      sx={{
        maxHeight,
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      <Box
        sx={{
          flex: '0 1 auto',
          maxHeight: maxHeight - fadeHeight,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {children}
      </Box>
      <Box
        sx={(theme) => ({
          mt: -1,
          flexShrink: 0,
          height: fadeHeight,
          background: `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
        })}
      />
    </Box>
  );
}
